import React, { useEffect, useState, useCallback } from "react";
import { Plus, ListChecks, Loader2, X, Trash2, Copy, Bell, Lock, Unlock, FileText, FileSpreadsheet, Users, EyeOff, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useFeatureFlags, FeatureDisabled } from "@/lib/featureFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import GroupMultiSelect from "@/components/GroupMultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageHeader, { EmptyState, fmtDate } from "@/components/PageHeader";

function fmtDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ISO timestamp -> value for a <input type="datetime-local"> (local time).
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const EMPTY_FORM = { title: "", description: "", anonymous: false, optional: false, deadline: "", max_select: 1 };

// Read-only "None" row shown when the Choice is Optional (added automatically on save).
function NoneRow({ testId }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid={testId}>
      <span className="font-medium">None</span><span className="text-xs text-slate-400">added automatically</span>
    </div>
  );
}
const newItem = (n) => ({ name: `Item ${n}`, options: ["", ""], max_select: 1 });

// Returns a valid pick limit (1..count) or null when out of range.
function parseAllowed(v, count) {
  const n = parseInt(v, 10);
  return !isNaN(n) && n >= 1 && n <= count ? n : null;
}

function SettingToggle({ title, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3">
      <div><p className="text-sm font-medium text-slate-800">{title}</p><p className="text-xs text-slate-400">{hint}</p></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

function ChoicesAllowed({ value, onChange, count, hint, testId, small }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Label className={small ? "text-xs text-slate-500" : ""}>Choices Allowed</Label>
      <Input type="number" min={1} max={Math.max(1, count)} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-8 w-20" data-testid={testId} />
      <span className="text-xs text-slate-400">{hint}</span>
    </div>
  );
}

function CreateDialog({ open, setOpen, subs, onDone, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [menuMode, setMenuMode] = useState(false);
  const [options, setOptions] = useState(["", ""]);
  const [cats, setCats] = useState([newItem(1)]);
  const [selSubs, setSelSubs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const isMenu = (initial.categories || []).length > 0;
      const flatOpts = isMenu ? [] : (initial.options || []).filter((o) => !o.none).map((o) => o.label);
      setForm({
        title: initial.title ? `${initial.title} (copy)` : "",
        description: initial.description || "",
        anonymous: !!initial.anonymous,
        optional: !!initial.optional,
        deadline: toLocalInput(initial.deadline),
        max_select: initial.max_select ?? (initial.multi_select ? Math.max(1, flatOpts.length) : 1),
      });
      setMenuMode(isMenu);
      setOptions(isMenu ? ["", ""] : flatOpts);
      setCats(isMenu
        ? initial.categories.map((cat) => ({ name: cat.name, options: (cat.options || []).filter((o) => !o.none).map((o) => o.label), max_select: cat.max_select ?? 1 }))
        : [newItem(1)]);
      setSelSubs(initial.subgroup_ids || []);
    } else {
      setForm(EMPTY_FORM);
      setMenuMode(false); setOptions(["", ""]); setCats([newItem(1)]); setSelSubs([]);
    }
  }, [open, initial]);

  const setOpt = (i, v) => setOptions((p) => p.map((o, idx) => (idx === i ? v : o)));
  const addOpt = () => setOptions((p) => [...p, ""]);
  const minOpts = form.optional ? 1 : 2;
  const removeOpt = (i) => setOptions((p) => (p.length <= minOpts ? p : p.filter((_, idx) => idx !== i)));

  // item helpers
  const setCatName = (ci, v) => setCats((p) => p.map((c, i) => (i === ci ? { ...c, name: v } : c)));
  const setCatMax = (ci, v) => setCats((p) => p.map((c, i) => (i === ci ? { ...c, max_select: v } : c)));
  const setCatOpt = (ci, oi, v) => setCats((p) => p.map((c, i) => (i === ci ? { ...c, options: c.options.map((o, j) => (j === oi ? v : o)) } : c)));
  const addCatOpt = (ci) => setCats((p) => p.map((c, i) => (i === ci ? { ...c, options: [...c.options, ""] } : c)));
  const removeCatOpt = (ci, oi) => setCats((p) => p.map((c, i) => (i === ci && c.options.length > 1 ? { ...c, options: c.options.filter((_, j) => j !== oi) } : c)));
  const addCat = () => setCats((p) => [...p, newItem(p.length + 1)]);
  const removeCat = (ci) => setCats((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== ci)));

  // With Optional on, a typed "None" is dropped — the automatic None replaces it.
  const filled = (list) => list.map((o) => o.trim()).filter((o) => o && !(form.optional && o.toLowerCase() === "none"));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Enter a choice title"); return; }
    const payload = {
      title: form.title.trim(), description: form.description.trim() || null,
      anonymous: form.anonymous,
      optional: form.optional,
      subgroup_ids: selSubs.length ? selSubs : null,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
    };
    if (menuMode) {
      const cleaned = cats
        .map((c) => ({ name: c.name.trim(), options: filled(c.options), max_select: c.max_select }))
        .filter((c) => c.name || c.options.length);
      if (cleaned.length === 0 || cleaned.some((c) => !c.name)) { toast.error("Give every item a name"); return; }
      if (cleaned.some((c) => c.options.length === 0)) { toast.error("Each item needs at least one choice"); return; }
      for (const c of cleaned) {
        const ms = parseAllowed(c.max_select, c.options.length);
        if (!ms) { toast.error(`Choices Allowed for "${c.name}" must be 1 to ${c.options.length}`); return; }
        c.max_select = ms;
      }
      payload.categories = cleaned;
    } else {
      const opts = filled(options);
      if (opts.length + (form.optional ? 1 : 0) < 2 || !opts.length) { toast.error(form.optional ? "Add at least one choice" : "Add at least two choices"); return; }
      const ms = parseAllowed(form.max_select, opts.length);
      if (!ms) { toast.error(`Choices Allowed must be 1 to ${opts.length}`); return; }
      payload.options = opts;
      payload.max_select = ms;
    }
    setLoading(true);
    try {
      await api.post("/coach/choices", payload);
      toast.success("Choice posted"); setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="choice-create-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Duplicate Choice" : "New Choice"}</DialogTitle>
          <DialogDescription>{initial ? "Edit this copy, then post it — members are notified only when you post." : "Post a Choice for your team to respond to."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Choice Title</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Meet-day food order" data-testid="choice-title-input" />
          </div>
          <div className="space-y-2"><Label>Description (optional)</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="choice-description-input" />
          </div>

          <div className="space-y-2"><Label>Settings</Label>
            <div className="divide-y divide-slate-200 rounded-lg border border-slate-200" data-testid="choice-settings">
              <SettingToggle title="Anonymous Responses" hint="You'll see totals only — not who picked what"
                checked={form.anonymous} onChange={(v) => setForm((f) => ({ ...f, anonymous: v }))} testId="choice-anonymous-switch" />
              <SettingToggle title="Multiple Items" hint="e.g. Entrée · Side · Drink — each item has its own choices and limit"
                checked={menuMode} onChange={setMenuMode} testId="choice-menu-switch" />
              <SettingToggle title="Optional" hint={`Adds a "None" choice${menuMode ? " to each item" : ""} — picking it clears any other pick`}
                checked={form.optional} onChange={(v) => setForm((f) => ({ ...f, optional: v }))} testId="choice-optional-switch" />
            </div>
          </div>

          {menuMode ? (
            <div className="space-y-4" data-testid="choice-categories">
              {cats.map((c, ci) => (
                <div key={ci} className="rounded-lg border border-slate-200 p-3" data-testid={`choice-category-${ci}`}>
                  <div className="flex items-center gap-2">
                    <Input value={c.name} onChange={(e) => setCatName(ci, e.target.value)} onFocus={(e) => e.target.select()} placeholder="Item name (e.g. Entrée)" className="font-medium" data-testid={`choice-category-name-${ci}`} />
                    <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-slate-400 disabled:opacity-30" disabled={cats.length <= 1} onClick={() => removeCat(ci)} data-testid={`choice-category-remove-${ci}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="mt-2 pl-2">
                    <ChoicesAllowed small value={c.max_select} onChange={(v) => setCatMax(ci, v)} count={filled(c.options).length}
                      hint="how many choices a member can pick from this item" testId={`choice-category-max-${ci}`} />
                  </div>
                  <div className="mt-2 space-y-2 pl-2">
                    {c.options.map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Input value={o} onChange={(e) => setCatOpt(ci, oi, e.target.value)} placeholder={`Choice ${oi + 1}`} data-testid={`choice-category-${ci}-option-${oi}`} />
                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-slate-400 disabled:opacity-30" disabled={c.options.length <= 1} onClick={() => removeCatOpt(ci, oi)}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    {form.optional && <NoneRow testId={`choice-category-${ci}-none`} />}
                    <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => addCatOpt(ci)} data-testid={`choice-category-${ci}-add-option`}><Plus className="mr-1 h-3.5 w-3.5" /> Add Choice</Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addCat} data-testid="choice-add-category-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add Item</Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Choices</Label>
                <div className="space-y-2" data-testid="choice-options">
                  {options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Choice ${i + 1}`} data-testid={`choice-option-input-${i}`} />
                      <Button type="button" size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-slate-400 disabled:opacity-30" disabled={options.length <= minOpts} onClick={() => removeOpt(i)} data-testid={`choice-option-remove-${i}`}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {form.optional && <NoneRow testId="choice-none-row" />}
                </div>
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addOpt} data-testid="choice-add-option-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add Choice</Button>
              </div>
              <ChoicesAllowed value={form.max_select} onChange={(v) => setForm((f) => ({ ...f, max_select: v }))} count={filled(options).length}
                hint="how many choices a member can pick" testId="choice-max-input" />
            </>
          )}

          <div className="space-y-2"><Label>Deadline (optional)</Label>
            <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm((f) => ({ ...f, deadline: e.target.value }))} data-testid="choice-deadline-input" />
          </div>
          {subs.length > 0 && (
            <div className="space-y-2">
              <Label>Send to</Label>
              <GroupMultiSelect subs={subs} value={selSubs} onChange={setSelSubs} allLabel="Entire team" testId="choice-audience-select" />
              <p className="text-xs text-slate-400">{selSubs.length ? "Only members in the selected group(s) will see this choice." : "Reaches every active team member."}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={loading} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="choice-create-submit-btn">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {initial ? "Post copy" : "Post Choice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MenuResults({ r }) {
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Totals by item</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="choice-menu-totals">
          {r.categories.map((cat) => (
            <div key={cat.id} className="rounded-lg border border-slate-200 p-3">
              <p className="mb-1.5 text-sm font-bold text-slate-800">{cat.name}</p>
              <div className="space-y-1">
                {cat.options.map((o) => (
                  <div key={o.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{o.label}</span>
                    <span className="tabular-nums font-semibold text-slate-900">{o.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {!r.anonymous && (r.member_rows || []).length > 0 && (
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Responses by member</p>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="choice-menu-member-table">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Member</th>
                  {r.category_names.map((n) => <th key={n} className="px-3 py-2">{n}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {r.member_rows.map((mr, i) => (
                  <tr key={i} data-testid={`choice-member-row-${i}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{mr.name}</td>
                    {mr.cells.map((c, ci) => <td key={ci} className="px-3 py-2 text-slate-600">{c.join(", ") || "—"}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function FlatResults({ r }) {
  const showPercent = !r.multi_select;
  return (
    <div className="space-y-4">
      <div className="space-y-3" data-testid="choice-results-tally">
        {r.options.map((o) => (
          <div key={o.id}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-800">{o.label}</span>
              <span className="tabular-nums text-slate-500">{o.count}{showPercent ? ` · ${o.percent}%` : ""}</span>
            </div>
            {showPercent && (
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-slate-900 transition-all" style={{ width: `${o.percent}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
      {!r.anonymous && (r.member_rows || []).length > 0 && (
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Responses by member</p>
          <div className="space-y-1" data-testid="choice-flat-member-list">
            {r.member_rows.map((mr, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <span className="font-medium text-slate-800">{mr.name}</span>
                {mr.picks.map((p) => <span key={p} className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{p}</span>)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultsDialog({ choiceId, open, setOpen, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!choiceId) return;
    const { data } = await api.get(`/coach/choices/${choiceId}`);
    setData(data);
  }, [choiceId]);
  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  const toggleStatus = async () => {
    setBusy(true);
    try {
      const action = data.choice.status === "open" ? "close" : "reopen";
      await api.post(`/coach/choices/${choiceId}/${action}`);
      toast.success(action === "close" ? "Submissions ended" : "Choice reopened");
      await load(); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [reminding, setReminding] = useState(false);
  const remind = async () => {
    setReminding(true);
    try {
      const { data: res } = await api.post(`/coach/choices/${choiceId}/remind`);
      toast.success(res.recipients ? res.message : "Everyone has already responded — no reminders needed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setReminding(false); }
  };

  const ch = data?.choice; const r = data?.results;

  const download = async (fmt) => {
    try {
      const res = await api.get(`/coach/choices/${choiceId}/export?fmt=${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      const safe = (ch?.title || "export").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "") || "export";
      a.download = `choice-${safe}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Unable to export"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="choice-results-dialog">
        <DialogHeader><DialogTitle className="font-heading">{ch?.title || "Results"}</DialogTitle>
          <DialogDescription>Tallied responses{ch?.anonymous ? " (anonymous)" : ""}. Export, end submissions or reopen below.</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {ch.description && <p className="text-sm text-slate-600">{ch.description}</p>}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"><Users className="h-3.5 w-3.5" /> {r.total_responses} / {r.targeted_count} responded</span>
              {ch.anonymous && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700"><EyeOff className="h-3.5 w-3.5" /> Anonymous</span>}
              {ch.multi_select && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{r.mode === "menu" ? "Menu" : "Multi-select"}</span>}
              <span className={`rounded-full px-2 py-0.5 ${ch.accepting ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{ch.accepting ? "Open" : "Closed"}</span>
            </div>

            {r.mode === "menu" ? <MenuResults r={r} /> : <FlatResults r={r} />}

            {!ch.anonymous && r.non_responders?.length > 0 && (
              <p className="text-xs text-slate-400"><span className="font-semibold text-slate-500">Not yet responded:</span> {r.non_responders.join(", ")}</p>
            )}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => download("pdf")} data-testid="choice-export-pdf-btn"><FileText className="mr-1 h-3.5 w-3.5" /> PDF</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => download("csv")} data-testid="choice-export-csv-btn"><FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> CSV</Button>
              {ch.accepting && !ch.anonymous && r.non_responders?.length > 0 && (
                <Button variant="outline" size="sm" className="rounded-full" onClick={remind} disabled={reminding} data-testid="choice-remind-btn">
                  {reminding ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Bell className="mr-1 h-3.5 w-3.5" />} Remind {r.non_responders.length} non-responder{r.non_responders.length === 1 ? "" : "s"}
                </Button>
              )}
              <Button variant="outline" size="sm" className="rounded-full" onClick={toggleStatus} disabled={busy} data-testid="choice-toggle-status-btn">
                {ch.status === "open" ? <><Lock className="mr-1 h-3.5 w-3.5" /> End Submissions</> : <><Unlock className="mr-1 h-3.5 w-3.5" /> Reopen</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChoiceRow({ c, collapsible = false, onDelete, onResults, onDuplicate, dupId }) {
  const [expanded, setExpanded] = useState(!collapsible);
  const isMenu = (c.categories || []).length > 0;
  const optCount = isMenu ? c.categories.reduce((s, cat) => s + (cat.options?.length || 0), 0) : (c.options?.length || 0);
  return (
    <Card className="border-slate-200 p-4 shadow-sm" data-testid={`choice-row-${c.id}`}>
      <div className={`flex items-start justify-between gap-2 ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={collapsible ? () => setExpanded((v) => !v) : undefined}
        data-testid={collapsible ? `choice-toggle-${c.id}` : undefined}>
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900">{c.title}</p>
          <p className="mt-0.5 text-xs text-slate-400">{isMenu ? `${c.categories.length} ${c.categories.length === 1 ? "item" : "items"} · ${optCount} choices` : `${optCount} choices`} · {fmtDate(c.created_at)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {collapsible && (
            <>
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"><Users className="h-3 w-3" /> {c.response_count}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs ${c.accepting ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{c.accepting ? "Open" : "Closed"}</span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </>
          )}
          {!collapsible && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => onDelete(c)} data-testid={`choice-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
          )}
        </div>
      </div>
      {expanded && (
        <>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600"><Users className="h-3 w-3" /> {c.response_count}</span>
            <span className={`rounded-full px-2 py-0.5 ${c.accepting ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{c.accepting ? "Open" : "Closed"}</span>
            {isMenu && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-700">Menu</span>}
            {c.anonymous && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700"><EyeOff className="h-3 w-3" /> Anonymous</span>}
            {c.group_names?.map((g) => <span key={g} className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{g}</span>)}
            {c.deadline && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">Closes {fmtDeadline(c.deadline)}</span>}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 rounded-full" onClick={() => onResults(c.id)} data-testid={`choice-view-results-${c.id}`}>
              <ListChecks className="mr-1.5 h-3.5 w-3.5" /> View results
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" disabled={dupId === c.id} onClick={() => onDuplicate(c)} data-testid={`choice-duplicate-${c.id}`} title="Duplicate as a fresh open copy">
              {dupId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            {collapsible && (
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-red-500" onClick={() => onDelete(c)} data-testid={`choice-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

export default function CoachChoices() {
  const { choices: choicesOn } = useFeatureFlags();
  if (!choicesOn) return <FeatureDisabled name="Choices" />;
  return <CoachChoicesInner />;
}

function CoachChoicesInner() {
  const [items, setItems] = useState([]);
  const [subs, setSubs] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [dupSource, setDupSource] = useState(null);
  const [resultsId, setResultsId] = useState(null);
  const [del, setDel] = useState(null);
  const [closedPage, setClosedPage] = useState(1);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/coach/choices"), api.get("/coach/subgroups")]);
    setItems(a.data.items || []); setSubs(b.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    try {
      await api.delete(`/coach/choices/${del.id}`);
      toast.success("Choice deleted"); setDel(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setDel(null); }
  };

  // Duplicating opens the create dialog prefilled so the coach can edit before posting
  // (the notification is only sent when the copy is posted).
  const openDuplicate = (c) => { setDupSource(c); setCreateOpen(true); };

  const CLOSED_PAGE_SIZE = 2;
  const openItems = items.filter((c) => c.accepting);
  const closedItems = items
    .filter((c) => !c.accepting)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const totalClosedPages = Math.max(1, Math.ceil(closedItems.length / CLOSED_PAGE_SIZE));
  const curClosed = Math.min(closedPage, totalClosedPages);
  const closedRows = closedItems.slice((curClosed - 1) * CLOSED_PAGE_SIZE, curClosed * CLOSED_PAGE_SIZE);

  return (
    <div>
      <PageHeader title="Choices" subtitle="Post a prompt for your team to respond to — food orders, voting, attendance and more."
        actions={<Button onClick={() => { setDupSource(null); setCreateOpen(true); }} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="new-choice-btn"><Plus className="mr-2 h-4 w-4" /> New Choice</Button>} />

      {items.length === 0 ? (
        <EmptyState title="No choices yet" description="Post your first prompt for the team." testId="choices-empty" />
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Open prompts</h3>
            {openItems.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid="choices-no-open">No open prompts right now.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2" data-testid="choices-list">
                {openItems.map((c) => (
                  <ChoiceRow key={c.id} c={c} onDelete={setDel} onResults={setResultsId} onDuplicate={openDuplicate} dupId={null} />
                ))}
              </div>
            )}
          </div>

          {closedItems.length > 0 && (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Ended prompts</h3>
                <span className="text-xs text-slate-400" data-testid="closed-page-label">Page {curClosed} of {totalClosedPages}</span>
              </div>
              <div className="space-y-2" data-testid="choices-closed-list">
                {closedRows.map((c) => (
                  <ChoiceRow key={c.id} c={c} collapsible onDelete={setDel} onResults={setResultsId} onDuplicate={openDuplicate} dupId={null} />
                ))}
              </div>
              {totalClosedPages > 1 && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" disabled={curClosed <= 1} onClick={() => setClosedPage((p) => Math.max(1, p - 1))} data-testid="closed-prev"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm text-slate-500">{curClosed} / {totalClosedPages}</span>
                  <Button variant="outline" size="sm" className="rounded-full" disabled={curClosed >= totalClosedPages} onClick={() => setClosedPage((p) => Math.min(totalClosedPages, p + 1))} data-testid="closed-next"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <CreateDialog open={createOpen} setOpen={setCreateOpen} subs={subs} onDone={load} initial={dupSource} />
      <ResultsDialog choiceId={resultsId} open={!!resultsId} setOpen={(o) => !o && setResultsId(null)} onChanged={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}
        title="Delete this choice?" description={del ? `"${del.title}" and all responses will be permanently removed.` : ""}
        confirmText="Delete" destructive onConfirm={doDelete} testId="choice-delete-confirm" />
    </div>
  );
}
